<template>
  <v-menu
    v-model="addMenu"
    left
    :close-on-content-click="false"
    min-width="500"
  >
    <template #activator="{ attrs, on }">
      <v-btn
        v-bind="attrs"
        v-on="on"
        :disabled="!types.length"
      >
        {{ $t('locationCreateDialog.createButtonLabel') }}
        <v-icon right> mdi-plus </v-icon>
      </v-btn>
    </template>
    <v-card>
      <v-card-title>
        {{ $t('locationCreateDialog.createButtonLabel') }}
      </v-card-title>
      <v-card-text>
        <v-form
          ref="addForm"
          v-model="addForm"
        >
          <v-text-field
            v-model="newLocation.title"
            :label="$t('common.name')"
            autofocus
            :rules="[ v => !!v || $t('common.requiredField') ]"
            outlined
            dense
          />
          <v-text-field
            v-model="newLocation.initials"
            :label="$t('common.initials')"
            :rules="[ v => !!v || $t('common.requiredField'), v => String(v).length <= 5 || $t('feedback.maxCharacters', { max: 5 }) ]"
            :hint="$t('feedback.unique')"
            persistent-hint
            outlined
            dense
          />
          <v-autocomplete
            v-model="newLocation.locationTypeId"
            :items="types"
            :label="$tc('common.type')"
            item-value="id"
            item-text="title"
            :rules="[ v => !!v || $t('common.requiredField') ]"
            clearable
            auto-select-first
            outlined
            dense
          />
          <v-autocomplete
            v-model="newLocation.countryId"
            :items="countries"
            :loading="loading.countries"
            :label="$t('common.country')"
            item-value="id"
            item-text="name"
            :rules="[ v => !!v || $t('common.requiredField') ]"
            clearable
            auto-select-first
            outlined
            dense
          />
          <v-text-field
            v-model="newLocation.address"
            :label="$t('common.address')"
            outlined
            dense
          />
          <v-select
            v-model="newLocation.locationAreaId"
            :items="areas"
            :label="$tc('common.area')"
            item-text="name"
            item-value="id"
            outlined
            dense
            clearable
          />
          <v-combobox
            v-model="newLocation.externalReferences"
            :label="$t('common.externalReference')"
            :hint="$t('hints.externalReference')"
            persistent-hint
            outlined
            clearable
            multiple
            small-chips
            deletable-chips
            dense
          />
          <v-divider class="mt-2"></v-divider>
          <h4 class="my-3">
            {{ $t('common.contactInfo') }}
          </h4>
          <v-text-field
            v-model="newLocation.contactName"
            :label="$t('common.name')"
            outlined
            dense
          />
          <v-text-field
            v-model="newLocation.contactEmail"
            :label="$t('common.email')"
            type="email"
            :rules="[rules.email]"
            outlined
            dense
          />
          <v-text-field
            v-model="newLocation.contactPhone"
            :label="$t('common.phone')"
            outlined
            dense
          />
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-btn
          text
          @click="clear(false); addMenu = false;"
          small
        >
          {{ $t('common.cancel') }}
        </v-btn>
        <v-btn text small @click="clear(true)">
          {{ $t('common.clear') }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          :disabled="!addForm"
          :loading="creating"
          @click="create"
          small
        >
          {{ $t('common.create') }}
          <v-icon right> mdi-plus </v-icon>
        </v-btn>
        <v-btn
          :disabled="!addForm"
          :loading="creating"
          @click="create(true)"
          small
        >
          {{ $t('common.createAndNew') }}
          <v-icon right> mdi-plus </v-icon>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<script>
import { email } from '@/helpers/rules';

export default {
  name: 'CustomerLocationCreateDialog',
  props: {
    customerId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      addMenu: false,
      addForm: false,
      loading: {
        types: true,
        countries: true,
        areas: true
      },
      creating: false,
      newLocation: {},
      countries: [],
      types: [],
      areas: [],
      rules: {
        email
      }
    }
  },
  mounted() {
    this.getCountries();
    this.getTypes();
    this.getAreas();
  },
  methods: {
    create: async function(leaveOpen = false) {
      this.creating = true;

      try {
        const model = {
          ...this.newLocation,
          customerId: this.customerId
        };

        const { data } = await this.$api.post(`locations`, model);

        if (!data.success) throw data.errors?.at(0)?.message ?? data.message ?? 'Something went wrong';

        if (!leaveOpen || typeof leaveOpen !== 'boolean') {
          this.addMenu = false;
          this.clear(true);
        } else {
          this.clear(false);
        }

        this.$notifier.showSuccess({ message: this.$t('locationCreateDialog.createSuccess') });
        this.$emit('create:success')
      } catch (error) {
        console.error(error);
        this.$notifier.showError({ message: error });
      } finally {
        this.creating = false;
      }
    },
    getCountries: async function() {
      this.loading.countries = true;

      try {
        const { data } = await this.$api.get(`countries`);

        this.countries = data;
      } catch (error) {
        console.error(error);
        this.$notifier.showError({ message: this.$t('locationCreateDialog.errors.countries') });
      } finally {
        this.loading.countries = false;
      }
    },
    getTypes: async function() {
      this.loading.types = true;

      try {
        const { data } = await this.$api.get(`location-types`, {
          params: {
            customerId: this.customerId
          }
        });

        this.types = data;
      } catch (error) {
        console.error(error);
        this.$notifier.showError({ message: this.$t('locationCreateDialog.errors.countries') });
      } finally {
        this.loading.types = false;
      }
    },
    getAreas: async function() {
      this.loading.areas = true;

      try {
        const { data } = await this.$api.get(`location-areas`, {
          params: {
            customerId: this.customerId
          }
        });

        this.areas = data;
      } catch (error) {
        console.error(error);
        this.$notifier.showError({ message: this.$t('locationCreateDialog.errors.areas') });
      } finally {
        this.loading.areas = false;
      }
    },
    clear: function(clearAll) {
      if (clearAll) {
        this.newLocation = {};
      } else {
        this.newLocation.title = null;
        this.newLocation.initials = null;
      }

      this.$refs.addForm.resetValidation();
    },
  }
}
</script>